import React, { useEffect, useState } from 'react';
import {
  Box, Card, Typography, Button, TextField, Grid, Avatar, MenuItem,
  CircularProgress, Alert, Collapse, IconButton, Divider, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Switch, Chip, Dialog,
  DialogTitle, DialogContent, DialogActions, Tooltip, useMediaQuery, useTheme,
} from '@mui/material';
import {
  Settings as SettingsIcon, CloudUpload, Save, ExpandMore, Business,
  CalendarMonth, Add, Delete, Close, CheckCircleOutline, EventBusy,
} from '@mui/icons-material';
import axios from 'axios';
import {
  churchSettingsURL, churchSettingsUpdateURL,
  accountingPeriodsURL, accountingPeriodsAddURL,
  accountingPeriodsToggleURL, accountingPeriodsDeleteURL,
} from '../BaseURL';
import { useNotify } from '../notify';

const CURRENCIES = ['GHS', 'USD', 'EUR', 'GBP', 'NGN', 'KES', 'ZAR'];

const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('church_token')}` });

// ══════════════════ Collapsible section wrapper ══════════════════
function Section({ title, subtitle, icon, iconColor = 'primary.main', defaultOpen = true, action, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <Card sx={{ mb: { xs: 1.5, sm: 2.5 }, overflow: 'hidden' }}>
      <Box
        onClick={() => setOpen(v => !v)}
        sx={{
          display: 'flex', alignItems: 'center',
          gap: { xs: 1.5, sm: 2 },
          p: { xs: 1.5, sm: 2.25 }, cursor: 'pointer',
          transition: 'background 0.15s',
          '&:hover': { bgcolor: 'rgba(11,37,69,0.02)' },
        }}
      >
        <Box sx={{
          width: { xs: 38, sm: 44 }, height: { xs: 38, sm: 44 }, borderRadius: 1.5,
          bgcolor: iconColor, color: '#fff',
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}>
          {icon}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle1" fontWeight={700} lineHeight={1.2}>{title}</Typography>
          {subtitle && (
            <Typography
              variant="caption" color="text.secondary"
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              {subtitle}
            </Typography>
          )}
        </Box>
        {action && <Box onClick={e => e.stopPropagation()}>{action}</Box>}
        <IconButton size="small" onClick={(e) => { e.stopPropagation(); setOpen(v => !v); }}>
          <ExpandMore sx={{
            transition: 'transform 0.2s',
            transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
          }} />
        </IconButton>
      </Box>
      <Collapse in={open} timeout="auto" unmountOnExit>
        <Divider />
        <Box sx={{ p: { xs: 1.75, sm: 3 }, bgcolor: '#FBFCFD' }}>
          {children}
        </Box>
      </Collapse>
    </Card>
  );
}

// ══════════════════ Church info sub-form ══════════════════
function ChurchInfo({ onSaved }) {
  const notify = useNotify();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [form, setForm] = useState({
    name: '', address: '', phone: '', email: '', currency: 'GHS', logo: '',
  });

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(churchSettingsURL, { headers: headers() });
        if (res.data.success && res.data.data) {
          const d = res.data.data;
          setForm({
            name: d.name || '', address: d.address || '', phone: d.phone || '',
            email: d.email || '', currency: d.currency || 'GHS', logo: d.logo || '',
          });
        }
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    })();
  }, []);

  const change = (e) => setForm({ ...form, [e.target.name]: e.target.value });
  const handleLogo = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 500 * 1024) {
      notify.warning('Logo must be under 500 KB.', { title: 'File too large' });
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => setForm(f => ({ ...f, logo: ev.target.result }));
    reader.readAsDataURL(file);
  };

  const save = async () => {
    if (!form.name.trim()) {
      notify.warning('Church name is required.', { title: 'Missing details' });
      return;
    }
    setSaving(true);
    try {
      await axios.post(churchSettingsUpdateURL, form, { headers: headers() });
      onSaved && onSaved({ name: form.name, logo: form.logo });
      notify.success('Church information saved.', { title: 'Settings updated' });
    } catch (err) {
      notify.error(err.response?.data?.message || 'Save failed.', { title: 'Save failed' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={28} /></Box>;

  return (
    <Box>
      <Grid container spacing={{ xs: 2, sm: 3 }}>
        {/* Logo panel */}
        <Grid item xs={12} md={3}>
          <Box sx={{
            p: { xs: 2, sm: 2.5 },
            border: '1px solid #E2E8F0', borderRadius: 2, bgcolor: '#fff',
            display: 'flex', flexDirection: 'column',
            alignItems: 'center', gap: 1.5, height: '100%',
          }}>
            {form.logo ? (
              <Avatar
                src={form.logo}
                sx={{ width: { xs: 72, md: 120 }, height: { xs: 72, md: 120 }, border: '3px solid #E2E8F0', flexShrink: 0 }}
              />
            ) : (
              <Avatar sx={{ width: { xs: 72, md: 120 }, height: { xs: 72, md: 120 }, bgcolor: 'primary.main', flexShrink: 0 }}>
                <Business sx={{ fontSize: { xs: 32, md: 52 } }} />
              </Avatar>
            )}
            <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ letterSpacing: '0.08em', textTransform: 'uppercase', fontSize: 10 }}>
              Church Logo
            </Typography>
            <Button component="label" variant="outlined" size="small" startIcon={<CloudUpload />} sx={{ mt: 'auto' }}>
              {form.logo ? 'Change Logo' : 'Upload Logo'}
              <input type="file" hidden accept="image/*" onChange={handleLogo} />
            </Button>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>
              Max 500 KB · PNG or JPG
            </Typography>
          </Box>
        </Grid>

        {/* Form fields — two-column layout */}
        <Grid item xs={12} md={9}>
          <Grid container spacing={2.5}>
            <Grid item xs={12} md={6}>
              <TextField fullWidth required label="Church Name" name="name" value={form.name} onChange={change} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth label="Address" name="address" value={form.address} onChange={change} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth label="Phone" name="phone" value={form.phone} onChange={change} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth type="email" label="Email" name="email" value={form.email} onChange={change} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField select fullWidth label="Base Currency" name="currency" value={form.currency} onChange={change}>
                {CURRENCIES.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
              </TextField>
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      <Divider sx={{ my: 3 }} />
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant="contained" size="large" onClick={save} disabled={saving}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <Save />}
          sx={{ px: 3 }}
        >
          {saving ? 'Saving…' : 'Save Changes'}
        </Button>
      </Box>
    </Box>
  );
}

// ══════════════════ Accounting Periods sub-form ══════════════════
function AccountingPeriods() {
  const notify = useNotify();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [periods, setPeriods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState('');
  const now = new Date();
  const iso = (d) => d.toISOString().slice(0, 10);
  const yearStart = iso(new Date(now.getFullYear(), 0, 1));
  const yearEnd   = iso(new Date(now.getFullYear(), 11, 31));

  const [form, setForm] = useState({
    name: `FY ${now.getFullYear()}`, start_date: yearStart, end_date: yearEnd, is_active: true,
  });

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(accountingPeriodsURL, { headers: headers() });
      if (res.data.success) setPeriods(res.data.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openAdd = () => {
    setForm({
      name: `FY ${now.getFullYear()}`, start_date: yearStart, end_date: yearEnd, is_active: true,
    });
    setError('');
    setDialogOpen(true);
  };

  const save = async () => {
    setError('');
    if (!form.name.trim() || !form.start_date || !form.end_date) {
      setError('Name, start date and end date are required.'); return;
    }
    if (new Date(form.end_date) < new Date(form.start_date)) {
      setError('End date must be after start date.'); return;
    }
    setSaving(true);
    try {
      await axios.post(accountingPeriodsAddURL, form, { headers: headers() });
      setDialogOpen(false);
      load();
      notify.success(`${form.name} is ready. ${form.is_active ? 'It is now active.' : 'Activate it whenever you like.'}`, { title: 'Period added' });
    } catch (err) {
      const msg = err.response?.data?.message || 'Save failed.';
      setError(msg);
      notify.error(msg, { title: 'Could not add period' });
    } finally { setSaving(false); }
  };

  const toggle = async (p) => {
    try {
      await axios.post(accountingPeriodsToggleURL,
        { id: p.id, is_active: !p.is_active },
        { headers: headers() }
      );
      load();
      notify.info(
        `${p.name} is now ${!p.is_active ? 'active' : 'inactive'}.`,
        { title: 'Period updated', duration: 3000 }
      );
    } catch (err) {
      notify.error(err.response?.data?.message || 'Update failed.', { title: 'Update failed' });
    }
  };

  const remove = async (p) => {
    const ok = await notify.confirm({
      title: 'Delete accounting period?',
      message: `"${p.name}" will be removed. Existing transactions for this window are not deleted, but no new ones can be recorded in this range.`,
      confirmLabel: 'Delete period',
      severity: 'danger',
    });
    if (!ok) return;
    try {
      await axios.post(accountingPeriodsDeleteURL, { id: p.id }, { headers: headers() });
      load();
      notify.success(`${p.name} has been removed.`, { title: 'Period deleted' });
    } catch (err) {
      notify.error(err.response?.data?.message || 'Delete failed.', { title: 'Delete failed' });
    }
  };

  const activeCount = periods.filter(p => p.is_active).length;
  const fmtDate = (d) => d ? new Date(d).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : '—';

  return (
    <Box>
      <Alert severity="info" sx={{ mb: 2.5, borderRadius: 2 }}>
        Transactions (income and payments) must fall within an <b>active</b> period. When a fiscal year ends,
        create the next period and activate it.
      </Alert>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
        <Chip
          size="small"
          label={`${activeCount} active`}
          color={activeCount > 0 ? 'success' : 'error'}
          sx={{ fontWeight: 700 }}
        />
        <Typography variant="caption" color="text.secondary">
          {periods.length} period{periods.length === 1 ? '' : 's'} total
        </Typography>
        <Button
          size="small" variant="contained" startIcon={<Add />}
          onClick={openAdd} sx={{ ml: 'auto' }}
        >
          Add Period
        </Button>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={28} /></Box>
      ) : (
        <TableContainer component={Box} sx={{ border: '1px solid #E2E8F0', borderRadius: 1.5, overflowX: 'auto' }}>
          <Table size="small" sx={{ minWidth: 640 }}>
            <TableHead>
              <TableRow>
                <TableCell>Period Name</TableCell>
                <TableCell>Start Date</TableCell>
                <TableCell>End Date</TableCell>
                <TableCell align="center">Duration</TableCell>
                <TableCell align="center">Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {periods.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    No accounting periods yet. Add one to start recording transactions.
                  </TableCell>
                </TableRow>
              )}
              {periods.map(p => {
                const days = Math.round((new Date(p.end_date) - new Date(p.start_date)) / (1000 * 60 * 60 * 24)) + 1;
                return (
                  <TableRow key={p.id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {p.is_active
                          ? <CheckCircleOutline sx={{ fontSize: 18, color: 'success.main' }} />
                          : <EventBusy sx={{ fontSize: 18, color: 'text.secondary' }} />}
                        <Typography variant="body2" fontWeight={600}>{p.name}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell>{fmtDate(p.start_date)}</TableCell>
                    <TableCell>{fmtDate(p.end_date)}</TableCell>
                    <TableCell align="center">
                      <Chip size="small" label={`${days} days`} variant="outlined" />
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title={p.is_active ? 'Click to deactivate' : 'Click to activate'}>
                        <Switch checked={!!p.is_active} onChange={() => toggle(p)} size="small" />
                      </Tooltip>
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Delete period">
                        <IconButton size="small" color="error" onClick={() => remove(p)}>
                          <Delete fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Add dialog */}
      <Dialog
        open={dialogOpen} onClose={() => setDialogOpen(false)}
        maxWidth="sm" fullWidth fullScreen={isMobile}
        PaperProps={{ sx: { borderRadius: { xs: 0, sm: 2 } } }}
      >
        <DialogTitle sx={{
          display: 'flex', alignItems: 'center', gap: 2,
          background: 'linear-gradient(90deg, #0B2545, #13315C)', color: '#fff', py: 2.5,
        }}>
          <Box sx={{
            width: 36, height: 36, borderRadius: 1.5, bgcolor: '#C89211',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <CalendarMonth />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" fontWeight={700} lineHeight={1.2}>Add Accounting Period</Typography>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
              Define a named fiscal window
            </Typography>
          </Box>
          <IconButton onClick={() => setDialogOpen(false)} sx={{ color: '#fff' }}><Close /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert>}
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth required label="Period Name" placeholder="e.g. FY 2025"
                value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth required type="date" label="Start Date" InputLabelProps={{ shrink: true }}
                value={form.start_date} onChange={e => setForm({ ...form, start_date: e.target.value })}
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth required type="date" label="End Date" InputLabelProps={{ shrink: true }}
                value={form.end_date} onChange={e => setForm({ ...form, end_date: e.target.value })}
                size="small"
              />
            </Grid>
            <Grid item xs={12}>
              <Box sx={{
                display: 'flex', alignItems: 'center', gap: 1.5,
                p: 1.5, borderRadius: 1, bgcolor: '#F1F5F9',
              }}>
                <Switch
                  checked={form.is_active}
                  onChange={e => setForm({ ...form, is_active: e.target.checked })}
                />
                <Box>
                  <Typography variant="body2" fontWeight={600}>Set as active</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Transactions can only be recorded within an active period.
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={save} disabled={saving} sx={{ px: 3 }}>
            {saving ? <CircularProgress size={18} color="inherit" /> : 'Add Period'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ══════════════════ Page wrapper ══════════════════
export default function ChurchSettings({ onSaved }) {
  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', mb: 3, gap: 2, flexWrap: 'wrap' }}>
        <Box>
          <Typography variant="caption" color="secondary.main" fontWeight={700} sx={{ letterSpacing: '0.14em', textTransform: 'uppercase' }}>
            Configuration
          </Typography>
          <Typography variant="h4" fontWeight={800} sx={{ letterSpacing: '-0.02em', mt: 0.5 }}>
            Settings
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage church information, accounting periods, and system-wide preferences.
          </Typography>
        </Box>
      </Box>

      <Box>
        <Section
          title="Church Information"
          subtitle="Name, logo, contact details and base currency"
          icon={<Business />}
          iconColor="primary.main"
          defaultOpen
        >
          <ChurchInfo onSaved={onSaved} />
        </Section>

        <Section
          title="Accounting Periods"
          subtitle="Define fiscal windows — transactions must fall within an active period"
          icon={<CalendarMonth />}
          iconColor="secondary.main"
          defaultOpen
        >
          <AccountingPeriods />
        </Section>
      </Box>
    </Box>
  );
}
